import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Heart, PartyPopper, Wallet } from 'lucide-react';
import NotificationBellButton from '../../components/NotificationBellButton';
import { HostBottomNav } from '../../components/HostBottomNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const FONT = "'Inria Serif', serif";

const formatEuro = (value) => {
  const numeric = Number(value || 0);
  return Number.isInteger(numeric) ? `${numeric}` : numeric.toFixed(2).replace(/\.00$/, '');
};

export default function HostTipReceivedPage() {
  const navigate = useNavigate();
  const { tipId } = useParams();
  const [tip, setTip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  const token = localStorage.getItem('token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    const fetchTip = async () => {
      try {
        const response = await axios.get(`${API}/post-stay-tips/host/${tipId}`, { headers });
        setTip(response.data);
      } catch (error) {
        setErrorMessage(error.response?.data?.detail || 'Unable to load this tip right now.');
      } finally {
        setLoading(false);
      }
    };

    fetchTip();
  }, [headers, tipId]);

  const guestFirstAndLast = tip?.guest_name || 'Your guest';

  return (
    <div
      className="app-shell"
      data-testid="host-tip-received-page"
      style={{ backgroundColor: 'var(--app-bg)' }}
    >
      <div style={{ padding: '14px 18px 0', display: 'flex', justifyContent: 'flex-end' }}>
        <NotificationBellButton
          buttonTestId="host-tip-received-bell-button"
          popupTestId="host-tip-received-notification-popup"
        />
      </div>

      <div
        className="page-content"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          paddingTop: 0,
          paddingBottom: 110,
        }}
      >
        <img
          src="/allure-logo.png"
          alt="Allure"
          style={{ width: 120, height: 108, objectFit: 'contain', marginTop: 4 }}
        />

        {loading ? (
          <div
            data-testid="host-tip-received-loading-state"
            className="animate-spin"
            style={{
              width: 44,
              height: 44,
              borderRadius: '50%',
              border: '4px solid rgba(112, 63, 246, 0.12)',
              borderTopColor: '#703FF6',
              marginTop: 40,
            }}
          />
        ) : errorMessage ? (
          <div style={{ marginTop: 40, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 18 }}>
            <p
              data-testid="host-tip-received-error-message"
              style={{
                fontFamily: FONT,
                fontSize: 22,
                fontWeight: 700,
                color: 'var(--text)',
                textAlign: 'center',
                lineHeight: 1.35,
                margin: 0,
                maxWidth: 320,
              }}
            >
              {errorMessage}
            </p>
            <button
              type="button"
              data-testid="host-tip-received-error-done-button"
              onClick={() => navigate('/host/earnings')}
              style={{
                width: '100%',
                maxWidth: 280,
                border: 'none',
                borderRadius: 9999,
                background: '#04A628',
                color: '#FFFFFF',
                fontFamily: FONT,
                fontSize: 22,
                fontWeight: 700,
                padding: '13px 20px',
                cursor: 'pointer',
              }}
            >
              Done
            </button>
          </div>
        ) : (
          <>
            {/* Hero */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: 4, gap: 8 }}>
              <span
                aria-hidden
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  background: 'rgba(229, 57, 53, 0.10)',
                  color: '#E53935',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 8px 18px rgba(229, 57, 53, 0.20)',
                }}
              >
                <Heart size={28} strokeWidth={2.4} fill="#E53935" />
              </span>
              <p
                data-testid="host-tip-received-title"
                style={{
                  fontFamily: FONT,
                  fontSize: 26,
                  fontWeight: 700,
                  color: 'var(--text)',
                  textAlign: 'center',
                  lineHeight: 1.1,
                  margin: 0,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                You received a tip
                <PartyPopper size={22} strokeWidth={2.2} color="#F4C54E" />
              </p>
            </div>

            {/* Premium tip card */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.42, ease: 'easeOut' }}
              data-testid="host-tip-received-card"
              style={{
                width: 'calc(100% - 40px)',
                maxWidth: 360,
                marginTop: 22,
                background: 'var(--surface)',
                borderRadius: 26,
                border: '1px solid var(--card-border)',
                boxShadow: 'var(--premium-shadow-main)',
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              {/* soft holographic top wash */}
              <div
                aria-hidden
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 110,
                  background:
                    'linear-gradient(135deg, rgba(229, 57, 53, 0.10) 0%, rgba(244, 197, 78, 0.10) 50%, rgba(112, 63, 246, 0.10) 100%)',
                  pointerEvents: 'none',
                }}
              />

              <div style={{ position: 'relative', padding: '20px 22px' }}>
                <span
                  style={{
                    fontFamily: FONT,
                    fontSize: 11,
                    letterSpacing: 1.4,
                    textTransform: 'uppercase',
                    color: 'var(--text-2)',
                    fontWeight: 700,
                  }}
                >
                  From your guest
                </span>
                <p
                  data-testid="host-tip-received-guest-name"
                  style={{
                    fontFamily: FONT,
                    fontSize: 22,
                    fontWeight: 700,
                    color: 'var(--text)',
                    margin: '4px 0 0',
                  }}
                >
                  {guestFirstAndLast}
                </p>
                {tip.listing_name ? (
                  <p
                    data-testid="host-tip-received-listing-name"
                    style={{
                      fontFamily: FONT,
                      fontSize: 14,
                      color: 'var(--text-2)',
                      margin: '4px 0 0',
                    }}
                  >
                    {tip.listing_name}
                  </p>
                ) : null}

                <div style={{ borderTop: '1px dashed var(--card-border)', margin: '18px -22px 16px' }} />

                <span
                  style={{
                    fontFamily: FONT,
                    fontSize: 11,
                    letterSpacing: 1.4,
                    textTransform: 'uppercase',
                    color: 'var(--text-2)',
                    fontWeight: 700,
                  }}
                >
                  Tip amount
                </span>
                <p
                  data-testid="host-tip-received-amount"
                  style={{
                    fontFamily: FONT,
                    fontSize: 40,
                    fontWeight: 700,
                    color: 'var(--status-success-text)',
                    lineHeight: 1.1,
                    margin: '4px 0 0',
                    letterSpacing: -0.6,
                  }}
                >
                  €{formatEuro(tip.amount)}
                </p>
              </div>

              {/* Footer note */}
              <div
                style={{
                  position: 'relative',
                  background: 'var(--surface-2)',
                  padding: '12px 18px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  borderTop: '1px solid var(--card-border)',
                }}
              >
                <span
                  aria-hidden
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 9,
                    background: 'rgba(31, 142, 42, 0.10)',
                    color: 'var(--status-success-text)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Wallet size={16} strokeWidth={2.2} />
                </span>
                <p
                  data-testid="host-tip-received-payout-note"
                  style={{
                    margin: 0,
                    fontFamily: FONT,
                    fontSize: 13,
                    color: 'var(--text-2)',
                    lineHeight: 1.35,
                  }}
                >
                  Added to your next monthly Allure payout.
                </p>
              </div>
            </motion.div>

            <p
              data-testid="host-tip-received-subtext"
              style={{
                fontFamily: FONT,
                fontSize: 15,
                color: 'var(--text-2)',
                textAlign: 'center',
                lineHeight: 1.45,
                margin: '20px 24px 0',
                maxWidth: 320,
              }}
            >
              {(guestFirstAndLast.split(' ')[0] || 'Your guest')} wanted to thank you for a great stay.
              Your hospitality matters — keep it up!
            </p>

            <button
              type="button"
              data-testid="host-tip-received-done-button"
              onClick={() => navigate('/host/earnings')}
              style={{
                marginTop: 22,
                width: 'calc(100% - 40px)',
                maxWidth: 280,
                border: 'none',
                borderRadius: 9999,
                background: 'var(--accent-purple)',
                color: '#FFFFFF',
                fontFamily: FONT,
                fontSize: 17,
                fontWeight: 700,
                padding: '13px 20px',
                cursor: 'pointer',
                boxShadow: 'var(--premium-shadow-main)',
              }}
            >
              View my earnings
            </button>
          </>
        )}
      </div>

      <HostBottomNav />
    </div>
  );
}
